import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  Facebook,
  Globe,
  Instagram,
  Linkedin,
  MessageCircle,
  Palette,
  Twitter,
  Youtube,
} from "lucide-react";
import { useTheme } from "../../hooks/useTheme";
import footerConfig from "../../config/footerConfig";
import { errorDev } from '../../utils/logger.js';

const SOCIAL_ICONS = {
  X: Twitter,
  Twitter,
  LinkedIn: Linkedin,
  Instagram,
  YouTube: Youtube,
  Facebook,
  Whatsapp: MessageCircle,
};

function SocialLink({ platform, url, ariaLabel }) {
  const Icon = SOCIAL_ICONS[platform];
  if (!Icon) return null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
    >
      <Icon size={18} />
    </a>
  );
}

function MobileAccordion({ heading, links }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-gray-200 dark:border-neutral-800">
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between py-3 text-sm font-semibold text-gray-900 dark:text-white"
        aria-expanded={open}
      >
        {heading}
        <ChevronDown
          size={16}
          className={`text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <ul className="pb-3 space-y-2" role="list">
          {links.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                className="block text-sm text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors pl-1"
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ThemeDropdownControl() {
  const { theme, setTheme } = useTheme();

  return (
    <div className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-neutral-700 px-2.5 py-1.5 bg-gray-50 dark:bg-neutral-800/60">
      <Palette className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
      <select
        value={theme}
        onChange={(e) => setTheme(e.target.value)}
        aria-label="Select theme"
        className="bg-transparent text-xs text-gray-600 dark:text-gray-300 border-none focus:outline-none focus:ring-0 cursor-pointer appearance-none pr-1"
      >
        <option value="light" className="bg-white dark:bg-neutral-900">Light</option>
        <option value="dark" className="bg-white dark:bg-neutral-900">Dark</option>
        <option value="system" className="bg-white dark:bg-neutral-900">Auto</option>
      </select>
    </div>
  );
}

function LanguageControl() {
  const { i18n } = useTranslation();

  const handleChange = useCallback(
    (e) => {
      const code = e.target.value;
      i18n.changeLanguage(code);
      try {
        localStorage.setItem("language", code);
      } catch (err) { errorDev("Operation failed:", err);}
    },
    [i18n]
  );

  const languages = footerConfig.languages || [];

  return (
    <div className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-neutral-700 px-2.5 py-1.5 bg-gray-50 dark:bg-neutral-800/60">
      <Globe className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
      <select
        value={(i18n.language || "en").split("-")[0]}
        onChange={handleChange}
        aria-label="Select language"
        className="bg-transparent text-xs text-gray-600 dark:text-gray-300 border-none focus:outline-none focus:ring-0 cursor-pointer appearance-none pr-1"
      >
        {languages.map((lang) => (
          <option key={lang.code} value={lang.code} className="bg-white dark:bg-neutral-900">
            {lang.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function MeDashboardFooterPanel() {
  const { sections, social, copyright } = footerConfig;

  return (
    <section className="md:hidden mt-8 border-t border-gray-200 dark:border-neutral-800 pt-5 pb-1">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <ThemeDropdownControl />
        <LanguageControl />
      </div>

      <div>
        {Object.values(sections).map((section) => (
          <MobileAccordion
            key={section.heading}
            heading={section.heading}
            links={section.links}
          />
        ))}
      </div>

      <div className="flex items-center gap-1 mt-4">
        {social.map((item) => (
          <SocialLink key={item.platform} {...item} />
        ))}
      </div>

      <div className="border-t border-gray-200 dark:border-neutral-800 mt-4 pt-3 pb-1">
        <p className="text-xs text-gray-400 dark:text-gray-500 text-center">{copyright}</p>
      </div>
    </section>
  );
}
